import { z } from 'zod';
import { type Jurisdiction } from '../enums/index.js'; // SSOT existante — NE PAS redéclarer

/**
 * Ordre juridictionnel — LA distinction structurante du droit français (séparée depuis 1790).
 * `judiciaire` = ordre judiciaire (Cour de cassation ; privé + pénal → CC/CA/TJ/TCOM).
 * `administratif` = ordre administratif (Conseil d'État ; litiges avec l'administration → CE/CAA/TA).
 * Discriminateur métier porté sur chaque décision corpus. Remplace l'ancien `CorpusSource`.
 */
export const OrdreSchema = z.enum(['judiciaire', 'administratif']);
export type Ordre = z.infer<typeof OrdreSchema>;

/**
 * Contrat de réponse du viewer interne de décision (`GET /api/decisions/:ordre/:id`).
 *
 * WHY schéma partagé : le backend valide la sortie DB avec `.parse()` (fail-loud si colonne
 * manquante ou mauvais type — détecte un drift de schéma OVH immédiatement), et le frontend
 * consomme le même type sans duplication locale. `z.string().nullable()` reflète l'état réel
 * des deux tables : certaines colonnes (`ecli`, `chamber`, `publication`) sont nulles sur un
 * sous-ensemble des décisions, et `source_url` est vide à 100 % côté admin (cf. WHY decisions.routes.ts).
 *
 * Mapping DB → champ :
 *  • judiciaire  : court, numero, ecli, chamber, publication, location_canonical AS location,
 *                  source_url, to_char(date_decision) AS date, full_text
 *  • administratif : jurisdiction AS court, numero_dossier AS numero, ecli, NULL AS chamber,
 *                    CASE code_publication AS publication, juridiction_nom AS location,
 *                    source_url, to_char(date_lecture) AS date, full_text
 *
 * `location` (2026-06-19) : enrichit le titre du viewer avec la ville/le nom complet.
 *  - administratif : `juridiction_nom` = nom officiel complet AVEC ville pour TA/CAA
 *    (« Tribunal Administratif de Toulon ») ; pour le CE il vaut une formation
 *    (« Section du Contentieux ») → le formateur de titre ne le retient que s'il nomme une cour.
 *  - judiciaire : `location_canonical` = ville propre normalisée (« paris », « aubenas »).
 */
/**
 * Un maillon de chaîne procédurale = un `decisionLink` Judilibre normalisé.
 *
 * Champs confirmés par la probe de couverture du 2026-06-23 (endpoint `/decision`) : `title` est le
 * seul garanti par la spec, tout le reste est `.nullable()` (CC porte chamber/solution systématiquement,
 * CA/anciennes décisions plus pauvres — on ne s'y fie pas). `number` peut être un n° d'affaire OU un
 * n° de pourvoi (`U2314760`).
 *
 * Deux identifiants de lien :
 *  - `judilibreId` : le `source_id` Judilibre du maillon (extrait de `id`/`url` du decisionLink) s'il
 *    pointe vers une décision réelle → permet un lien externe `courdecassation.fr/decision/{id}`.
 *  - `corpusId`    : l'UUID interne SI ce `judilibreId` existe dans NOTRE corpus (`jp_decisions.source_id`)
 *    → permet un deep-link vers le viewer interne. Résolu hors parser (lookup batch), `null` sinon.
 */
export const ChainLinkSchema = z.object({
  title: z.string(),
  date: z.string().nullable(),
  jurisdiction: z.string().nullable(),
  chamber: z.string().nullable(),
  solution: z.string().nullable(),
  number: z.string().nullable(),
  judilibreId: z.string().nullable(),
  corpusId: z.string().uuid().nullable(),
});
export type ChainLink = z.infer<typeof ChainLinkSchema>;

/**
 * Chaîne procédurale d'une décision (Judilibre decisionFull). `contested` = décision attaquée
 * (chaînage arrière, ~91 % sur les arrêts de cassation) ; `timeline` = chaîne complète ordonnée par
 * date croissante (bonus, ~13 % CC) ; `forward` = rebond/pourvoi suivant (bonus, surtout CA).
 * Une chaîne « vide » = `{ contested: null, timeline: [], forward: null }` (décision sans chaîne, ou 404).
 */
export const ProceduralChainSchema = z.object({
  contested: ChainLinkSchema.nullable(),
  timeline: z.array(ChainLinkSchema),
  forward: ChainLinkSchema.nullable(),
});
export type ProceduralChain = z.infer<typeof ProceduralChainSchema>;

export const DecisionDetailSchema = z.object({
  id: z.string().uuid(),
  ordre: OrdreSchema,
  court: z.string().nullable(),
  numero: z.string().nullable(),
  date: z.string().nullable(),
  ecli: z.string().nullable(),
  chamber: z.string().nullable(),
  publication: z.string().nullable(),
  location: z.string().nullable(),
  sourceUrl: z.string().nullable(),
  fullText: z.string().nullable(),
  // Chaîne procédurale (judiciaire cc/ca uniquement, sinon null) — peuplée par le backfill, lue par
  // le viewer. `.nullable()` : null pour l'admin/non-backfillé ; jamais absent (le repo l'émet toujours).
  proceduralChain: ProceduralChainSchema.nullable(),
});
export type DecisionDetail = z.infer<typeof DecisionDetailSchema>;

// Tuples littéraux internes (`as const satisfies`) : `satisfies` rejette à la compilation
// tout code qui ne serait PAS un `Jurisdiction` (typo), tout en préservant les types
// littéraux exacts nécessaires à la garde de partition ci-dessous. Les exports gardent le
// type large `readonly Jurisdiction[]` pour ne rien changer côté consommateurs.
const ADMIN_CODES = ['CE', 'CAA', 'TA'] as const satisfies readonly Jurisdiction[];
const JUDICIAL_CODES = ['CC', 'CA', 'TJ', 'TCOM'] as const satisfies readonly Jurisdiction[];

/**
 * Codes de juridiction de l'ordre administratif (CE, CAA, TA).
 * Exporté pour que les callers puissent construire des Sets typés ou valider sans
 * dupliquer la liste. Ordre identique à `specialisationRouting.ts` (source de vérité A1).
 * `Object.freeze` : ces constantes sont partagées PAR RÉFÉRENCE (Sets internes, Map matière,
 * fallback de `resolveJurisdictions`) — le gel empêche qu'un caller mutant le tableau
 * (le `readonly` TS n'est que compile-time) corrompe silencieusement la SSOT à l'exécution.
 */
export const ADMIN_JURISDICTION_CODES: readonly Jurisdiction[] = Object.freeze(ADMIN_CODES);

/**
 * Codes de juridiction de l'ordre judiciaire (CC, CA, TJ, TCOM).
 * Même raison d'export + gel que `ADMIN_JURISDICTION_CODES`.
 */
export const JUDICIAL_JURISDICTION_CODES: readonly Jurisdiction[] = Object.freeze(JUDICIAL_CODES);

/**
 * Juridictions judiciaires cochées par défaut dans une recherche de jurisprudence.
 *
 * WHY cette liste est distincte de `JUDICIAL_JURISDICTION_CODES` : TJ et TCOM
 * restent des choix explicites valides, mais les inclure automatiquement ajoute
 * des décisions de première instance que l'avocat n'a pas forcément demandées.
 * Les recherches administratives gardent, elles, leur couverture CE/CAA/TA complète.
 */
const DEFAULT_JUDICIAL_SEARCH_CODES = ['CC', 'CA'] as const satisfies readonly Jurisdiction[];
export const DEFAULT_JUDICIAL_SEARCH_JURISDICTION_CODES: readonly Jurisdiction[] =
  Object.freeze(DEFAULT_JUDICIAL_SEARCH_CODES);

/**
 * Garde de PARTITION — purement compile-time, zéro empreinte runtime.
 * `ADMIN_CODES` ∪ `JUDICIAL_CODES` DOIT couvrir tout l'enum `Jurisdiction` (`enums/index.ts`).
 * Si un code est ajouté à `Jurisdiction` sans être ajouté à exactement un des deux tuples
 * ci-dessus, `_UncoveredJurisdiction` cesse d'être `never` et l'alias `_GuardJurisdictionPartition`
 * ne compile plus (« ne satisfait pas la contrainte never »). Empêche qu'une nouvelle juridiction
 * tombe silencieusement dans `DEFAULT_ORDRE = 'judiciaire'` via `resolveOrdre` — une décision
 * administrative routée vers le corpus judiciaire (review-loop 2026-06-21).
 */
type _AssertNever<T extends never> = T;
type _UncoveredJurisdiction = Exclude<Jurisdiction, (typeof ADMIN_CODES)[number] | (typeof JUDICIAL_CODES)[number]>;
/**
 * Preuve de partition (compile-time, exportée pour rester « utilisée » sous le `noUnusedLocals`
 * strict du backend qui compile cette source JIT). Vaut `never` tant que la couverture est totale ;
 * si une juridiction reste non couverte, l'instanciation de `_AssertNever` casse le build ici.
 */
export type JurisdictionPartitionProof = _AssertNever<_UncoveredJurisdiction>;

// Sets string internes pour ordreFromJurisdiction — le lookup est case-insensitive
// (la fonction trim+uppercase avant lookup) et Set.prototype.has est O(1).
// WHY Set<string> et non Set<Jurisdiction> : la fonction reçoit un `string` non validé,
// donc on ne peut pas typer le paramètre has() avec Jurisdiction sans un cast.
const ADMIN_SET = new Set<string>(ADMIN_JURISDICTION_CODES);
const JUDICIAL_SET = new Set<string>(JUDICIAL_JURISDICTION_CODES);

/**
 * Mappe un code juridiction (UI / court code Judilibre) vers son `Ordre`.
 *
 * WHY dans shared-types : cette fonction est le seul mapping `jurisdiction → Ordre` partagé
 * entre le backend (`jurisdictionRouting.ts`, `jpToolResultReplay.ts`) et potentiellement
 * le frontend (hector-link renderer). La définir ici garantit une source unique de vérité
 * et évite qu'une app diverge sur la liste des juridictions admin.
 *
 * WHY `null` (jamais d'élargissement silencieux) : une juridiction inconnue (ex CEDH, CJUE,
 * ou une typo) retourne `null` plutôt que de tomber silencieusement dans l'ordre judiciaire
 * par défaut. Le caller doit gérer `null` explicitement — evite qu'une décision CJUE soit
 * routée vers le corpus judiciaire.
 *
 * @param jurisdiction - code juridiction uppercase (CC, CA, TJ, TCOM, CE, CAA, TA) ou lowercase
 * @returns `'judiciaire'` | `'administratif'` | `null` si juridiction non reconnue
 */
export function ordreFromJurisdiction(jurisdiction: string): Ordre | null {
  const j = jurisdiction.trim().toUpperCase();
  if (ADMIN_SET.has(j)) return 'administratif';
  if (JUDICIAL_SET.has(j)) return 'judiciaire';
  return null;
}

/**
 * Politique historique : un dossier sans signal de matière est traité comme judiciaire.
 * Exporté pour que les callers utilisent la constante plutôt que de dupliquer le littéral
 * `'judiciaire'` — un seul endroit à changer si la politique évolue.
 */
export const DEFAULT_ORDRE: Ordre = 'judiciaire';

/**
 * Dérive l'ordre d'une juridiction avec fallback EXPLICITE.
 * Remplace les `?? 'judiciaire'` dispersés dans le codebase : le caller passe son
 * fallback (typiquement `DEFAULT_ORDRE`) et la politique reste lisible à l'appel.
 *
 * WHY ne pas coder le fallback en dur ici : certains callers futurs peuvent vouloir
 * un fallback `'administratif'` pour du traitement isolé (tests, import DB). Rendre
 * le fallback explicite évite des comportements silencieux.
 *
 * @param jurisdiction - code juridiction uppercase (CC, CA, TJ, TCOM, CE, CAA, TA) ou lowercase
 * @param fallback - ordre à retourner si `jurisdiction` est inconnue
 * @returns l'ordre résolu, jamais null
 */
export function resolveOrdre(jurisdiction: string, fallback: Ordre): Ordre {
  return ordreFromJurisdiction(jurisdiction) ?? fallback;
}

/**
 * Normalise un label matière (trim + lowercase + suppression diacritiques NFD).
 * Idempotente. Reproduit `normalizeLabel()` de `specialisationRouting.ts`.
 *
 * WHY NFD + regex [\u0300-\u036f] : décompose les caractères accentués en base +
 * combining mark, puis supprime les marks (range Unicode des diacritiques combinants)
 * — plus fiable que NFC ou un remplacement ad-hoc. Range écrite en escapes Unicode
 * (et non en codepoints littéraux) pour rester lisible et robuste au ré-encodage du fichier.
 * WHY dans shared-types : A2 importera cette fonction pour éviter la duplication ; la
 * normalisation doit être identique sinon les clés Map ne matchent pas.
 *
 * @param label - label brut (ex. "Droit Pénal")
 * @returns label normalisé (ex. "droit penal")
 */
export function normalizeSpecialisationLabel(label: string): string {
  return label
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

/**
 * Mappe un label matière normalisé vers ses juridictions de recherche.
 *
 * WHY Map et non objet : un objet a une chaîne prototype ; `obj['__proto__']` retourne
 * `Object.prototype` au lieu de `undefined`. Comme le label vient de l'add-in (chaîne
 * user-supplied), utiliser une Map supprime toute surface d'attaque prototype-pollution.
 * (Même raison que dans `specialisationRouting.ts`.)
 *
 * Les clés DOIVENT être le résultat de `normalizeSpecialisationLabel()`. Les valeurs
 * DOIVENT rester en sync avec `legalCategories` dans l'add-in (HectorAI.tsx).
 *
 * WHY les alias 'droit administratif' / 'contentieux administratif' : `cases.procedure_type`
 * en DB OVH stocke historiquement « Droit Administratif » (label antérieur au picker
 * actuel « Droit Public »). Sans ces alias, un dossier admin en DB tombait sur le
 * défaut judiciaire et cherchait des JP de cassation pour un REP TA. Cf. bench iter-1
 * et commentaire source dans `specialisationRouting.ts`.
 */
const SPECIALISATION_TO_JURISDICTIONS = new Map<string, readonly Jurisdiction[]>([
  ['droit penal',                JUDICIAL_JURISDICTION_CODES],
  ['droit commercial',           JUDICIAL_JURISDICTION_CODES],
  ['droit civil',                JUDICIAL_JURISDICTION_CODES],
  ['droit social',               JUDICIAL_JURISDICTION_CODES],
  ['droit de la famille',        JUDICIAL_JURISDICTION_CODES],
  // Baux, copropriété, construction, ventes : ordre judiciaire. L'urbanisme
  // relève de « Droit Public ».
  ['droit immobilier et de la construction', JUDICIAL_JURISDICTION_CODES],
  // Dommage corporel, garantie d'assurance, appel en garantie : ordre judiciaire.
  ['droit des assurances et de la responsabilite civile', JUDICIAL_JURISDICTION_CODES],
  // Homologation d'accord, médiation judiciaire ordonnée par le juge : ordre judiciaire.
  ['mediation',                  JUDICIAL_JURISDICTION_CODES],
  ['droit public',              ADMIN_JURISDICTION_CODES],
  // Aliases (voir WHY ci-dessus)
  ['droit administratif',        ADMIN_JURISDICTION_CODES],
  ['contentieux administratif',  ADMIN_JURISDICTION_CODES],
]);

/**
 * Retourne les juridictions associées à un label matière, ou `null` si inconnu/vide.
 *
 * WHY `null` (pas un défaut judiciaire) : le caller A2 appliquera le défaut judiciaire
 * explicitement via `DEFAULT_ORDRE`. Retourner `null` ici permet au caller de distinguer
 * "matière inconnue" de "matière judiciaire connue" — utile pour logguer un `blind-default`.
 *
 * @param label - label matière brut (ex. "Droit Administratif") ou null/undefined
 * @returns tableau readonly de codes juridiction, ou `null` si label vide/inconnu
 */
export function jurisdictionsForSpecialisation(label?: string | null): readonly Jurisdiction[] | null {
  if (!label) return null;
  const key = normalizeSpecialisationLabel(label);
  if (!key) return null;
  return SPECIALISATION_TO_JURISDICTIONS.get(key) ?? null;
}

/**
 * Retourne les juridictions à cocher par défaut pour une matière.
 *
 * `jurisdictionsForSpecialisation()` décrit la couverture complète d'une matière
 * et reste volontairement inchangée pour les callers qui ont besoin de tous les
 * choix. Cette fonction porte la politique UX de recherche : ordre administratif
 * complet, ordre judiciaire limité à CC/CA, et défaut judiciaire si la matière
 * est absente ou inconnue.
 */
export function defaultSearchJurisdictionsForSpecialisation(
  label?: string | null,
): readonly Jurisdiction[] {
  const available = jurisdictionsForSpecialisation(label);
  if (available?.length && available.every((jurisdiction) => ADMIN_SET.has(jurisdiction))) {
    return available;
  }
  return DEFAULT_JUDICIAL_SEARCH_JURISDICTION_CODES;
}
